import { useEffect, useState } from 'react';

/** Small circular countdown showing seconds remaining on the move clock.
 *  Ticks every 100ms locally; the deadline is server-authoritative so the
 *  client just renders against it. Turns red in the last 10s. */
export function TurnCountdownRing({
  deadline,
  totalMs = 60_000,
  size = 24,
  inverted = false,
}: {
  deadline: number;
  totalMs?: number;
  size?: number;
  /** When true (opponent context) the ring/text use light colors so they
   *  read on a dark seat background. The default styles assume a gold-chip
   *  background. */
  inverted?: boolean;
}) {
  const [remainingMs, setRemainingMs] = useState(() => Math.max(0, deadline - Date.now()));
  useEffect(() => {
    const tick = () => setRemainingMs(Math.max(0, deadline - Date.now()));
    tick();
    const id = window.setInterval(tick, 100);
    return () => window.clearInterval(id);
  }, [deadline]);
  const pct = Math.max(0, Math.min(1, remainingMs / totalMs));
  const seconds = Math.ceil(remainingMs / 1000);
  const urgent = seconds <= 10;
  const half = size / 2;
  const r = half - 3;
  const c = 2 * Math.PI * r;
  const stroke = urgent ? '#ff6b6b' : inverted ? '#f5c14a' : '#0b1320';
  const track = inverted ? 'rgba(255,255,255,0.18)' : 'rgba(11,19,32,0.25)';
  const bg = inverted ? 'transparent' : 'rgba(11,19,32,0.30)';
  return (
    <span
      className="relative inline-flex items-center justify-center rounded-full"
      style={{ width: size, height: size, background: bg }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute inset-0">
        <circle cx={half} cy={half} r={r} stroke={track} strokeWidth="2" fill="none" />
        <circle
          cx={half}
          cy={half}
          r={r}
          stroke={stroke}
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          transform={`rotate(-90 ${half} ${half})`}
          style={{ transition: 'stroke-dashoffset 120ms linear, stroke 250ms ease' }}
        />
      </svg>
      <span
        className="font-display font-bold leading-none tabular-nums z-10"
        style={{ color: stroke, fontSize: Math.max(8, Math.round(size * 0.38)) }}
      >
        {seconds}
      </span>
    </span>
  );
}
